import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import chronologIcon from './assets/icons/chronolog_icon.png';
import { useAnalytics } from './hooks/useAnalytics';
import useTranslation from './hooks/useTranslation';

function Navigation({ showAndroidButton = false, onOpenModal }) {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Analytics and translation hooks
  const { trackNavigation } = useAnalytics();
  const { t, currentLanguage } = useTranslation();
  
  // Helper function to create links with language parameter
  const createLocalizedLink = (path) => {
    if (currentLanguage === 'de') {
      return `${path}${path.includes('?') ? '&' : '?'}lang=de`;
    }
    return path;
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
    // Track mobile menu toggle
    trackNavigation('mobile_menu', !showMobileMenu ? 'open' : 'close');
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMenu && !event.target.closest('.nav-links')) {
        setShowMobileMenu(false);
        trackNavigation('mobile_menu', 'close_outside_click');
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMobileMenu, trackNavigation]);

  const handleAndroidButtonClick = () => {
    trackNavigation('android_testing', 'click', { 
      location: showMobileMenu ? 'mobile_menu' : 'desktop_nav' 
    });
    if (onOpenModal) {
      onOpenModal();
    }
    setShowMobileMenu(false);
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <Link 
            to={createLocalizedLink("/")} 
            className="logo"
            onClick={() => trackNavigation('home', 'click', { location: 'logo' })}
          >
            <div className="app-icon">
              <img src={chronologIcon} alt="ChronoLog App Icon" />
            </div>
            <h1>ChronoLog</h1>
          </Link>
          
          <nav className="nav-links">
            <div className="desktop-nav">
              <Link 
                to={createLocalizedLink("/about")} 
                className="nav-link"
                onClick={() => trackNavigation('about', 'click', { location: 'desktop_nav' })}
              >
                {t('navigation.about', 'About')}
              </Link>
              <Link
                to={createLocalizedLink("/info#faq")}
                className="nav-link"
                onClick={() => trackNavigation('faq', 'click', { location: 'desktop_nav' })}
              >
                {t('navigation.faq', 'FAQ')}
              </Link>
              <Link
                to={createLocalizedLink("/guides")}
                className="nav-link"
                onClick={() => trackNavigation('guides', 'click', { location: 'desktop_nav' })}
              >
                {t('navigation.guides', 'Guides')}
              </Link>
              <Link
                to={createLocalizedLink("/timegrapher")}
                className="nav-link"
                onClick={() => trackNavigation('timegrapher', 'click', { location: 'desktop_nav' })}
              >
                {t('navigation.timegrapher', 'Timegrapher')}
              </Link>
              <Link
                to={createLocalizedLink("/analytics")}
                className="nav-link"
                onClick={() => trackNavigation('analytics', 'click', { location: 'desktop_nav' })}
              >
                {t('navigation.analytics', 'Analytics')}
              </Link>
              {showAndroidButton && (
                <button onClick={() => handleAndroidButtonClick()} className="android-testers-btn">
                  Android testers needed!
                </button>
              )}
            </div>
            
            {/* Mobile Android Button - Always Visible */}
            <div className="mobile-android-btn">
              {showAndroidButton && (
                <button onClick={() => handleAndroidButtonClick()} className="android-testers-btn mobile-always-visible">
                  Android testers needed!
                </button>
              )}
            </div>
            
            <button className="mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Toggle menu">
              <div className={`hamburger ${showMobileMenu ? 'active' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
            
            <div className={`mobile-nav ${showMobileMenu ? 'active' : ''}`}>
              <Link 
                to={createLocalizedLink("/about")} 
                className="mobile-nav-link" 
                onClick={() => {
                  trackNavigation('about', 'click', { location: 'mobile_nav' });
                  setShowMobileMenu(false);
                }}
              >
                {t('navigation.about', 'About')}
              </Link>
              <Link
                to={createLocalizedLink("/info#faq")}
                className="mobile-nav-link"
                onClick={() => {
                  trackNavigation('faq', 'click', { location: 'mobile_nav' });
                  setShowMobileMenu(false);
                }}
              >
                {t('navigation.faq', 'FAQ')}
              </Link>
              <Link
                to={createLocalizedLink("/guides")}
                className="mobile-nav-link"
                onClick={() => {
                  trackNavigation('guides', 'click', { location: 'mobile_nav' });
                  setShowMobileMenu(false);
                }}
              >
                {t('navigation.guides', 'Guides')}
              </Link>
              <Link
                to={createLocalizedLink("/timegrapher")}
                className="mobile-nav-link"
                onClick={() => {
                  trackNavigation('timegrapher', 'click', { location: 'mobile_nav' });
                  setShowMobileMenu(false);
                }}
              >
                {t('navigation.timegrapher', 'Timegrapher')}
              </Link>
              <Link
                to={createLocalizedLink("/analytics")}
                className="mobile-nav-link"
                onClick={() => {
                  trackNavigation('analytics', 'click', { location: 'mobile_nav' });
                  setShowMobileMenu(false);
                }}
              >
                {t('navigation.analytics', 'Analytics')}
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}

export default Navigation; 